import { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { format, differenceInSeconds } from 'date-fns';
import { analytics } from '@/utils/analytics';
import { Card, StatCard, Badge, Table, Pagination, VehicleImage } from '@/components';
import {
  useVehicle,
  useSessions,
  useNhtsaVinDecode,
} from '@/hooks/useProApi';
import { formatVehicle, formatVin, formatDuration, utcToLocal, type SessionSummary } from '@/types';
import { SectionHeader, LabelValue } from '@/components/vehicle/shared';
import { VehicleDataTabs, AIDiagnosticSummary } from '@/components/vehicle';

// Stat card icons (PNG)
import dashboardIcon from '@/icons/icons8-dashboard-64.png';
import errorIcon from '@/icons/icons8-error-50.png';
import clipboardIcon from '@/icons/icons8-clipboard-50.png';
import chartIcon from '@/icons/icons8-chart-50.png';

export function VehicleDetail() {
  const { vin } = useParams<{ vin: string }>();
  const navigate = useNavigate();
  const { data: vehicle, isLoading, error } = useVehicle(vin || '');
  const { data: sessionsData } = useSessions({ vin, pageSize: 1000 });

  // NHTSA auto-decode (free)
  const { data: nhtsaVehicleInfo, isLoading: nhtsaLoading } = useNhtsaVinDecode(vin);

  const [sessionPage, setSessionPage] = useState(1);
  const sessionPageSize = 10;

  // Analytics: Track vehicle viewed
  useEffect(() => {
    if (vin) {
      analytics.vehicleViewed(vin);
    }
  }, [vin]);

  // Sessions table data (hooks must be before early returns)
  const allSessions = useMemo(() => sessionsData?.sessions || [], [sessionsData]);
  const paginatedSessions = useMemo(() => {
    const start = (sessionPage - 1) * sessionPageSize;
    return allSessions.slice(start, start + sessionPageSize);
  }, [allSessions, sessionPage]);
  const sessionTotalPages = Math.ceil(allSessions.length / sessionPageSize);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-obdai-gradient-start"></div>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-bold text-white mb-2">Vehicle Not Found</h2>
        <p className="text-gray-400 mb-4">The requested vehicle could not be found.</p>
        <Link to="/vehicles" className="text-obdai-gradient-start hover:text-obdai-gradient-end">
          Back to Vehicles
        </Link>
      </div>
    );
  }

  // Count unique DTCs (for stat cards only)
  const dtcCounts = new Map<string, number>();
  vehicle.dtcHistory?.forEach((dtc) => {
    dtcCounts.set(dtc.code, (dtcCounts.get(dtc.code) || 0) + 1);
  });

  const sessionColumns = [
    {
      key: 'startTime',
      header: 'Start Time',
      render: (item: SessionSummary) => (
        <span className="text-sm">{format(utcToLocal(item.createdAt), 'MMM d, yyyy h:mm a')}</span>
      ),
    },
    {
      key: 'endTime',
      header: 'End Time',
      render: (item: SessionSummary) => (
        <span className="text-sm text-gray-400">
          {item.updatedAt ? format(utcToLocal(item.updatedAt), 'MMM d, yyyy h:mm a') : '-'}
        </span>
      ),
    },
    {
      key: 'duration',
      header: 'Duration',
      render: (item: SessionSummary) => {
        const durationSecs = item.updatedAt
          ? differenceInSeconds(utcToLocal(item.updatedAt), utcToLocal(item.createdAt))
          : 0;
        return (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-purple-400">
              {formatDuration(item.createdAt, item.updatedAt)}
            </span>
            {durationSecs < 60 && (
              <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-500/20 text-gray-400 border border-gray-500/30">
                Brief
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'dtcCount',
      header: 'DTCs',
      render: (item: SessionSummary) => (
        <Badge variant={item.dtcCount > 0 ? 'error' : 'success'}>
          {item.dtcCount} {item.dtcCount === 1 ? 'code' : 'codes'}
        </Badge>
      ),
    },
    {
      key: 'hasReport',
      header: 'Report',
      render: (item: SessionSummary) =>
        item.hasReport ? (
          <Badge variant="info">Saved</Badge>
        ) : (
          <span className="text-gray-500">-</span>
        ),
    },
  ];

  // Build compact NHTSA summary
  const nhtsaSummary = nhtsaVehicleInfo
    ? [
        nhtsaVehicleInfo.year,
        nhtsaVehicleInfo.make,
        nhtsaVehicleInfo.model,
        nhtsaVehicleInfo.trim,
        nhtsaVehicleInfo.displacementL ? `${nhtsaVehicleInfo.displacementL}L` : null,
        nhtsaVehicleInfo.engineCylinders ? `${nhtsaVehicleInfo.engineCylinders}-cyl` : null,
        nhtsaVehicleInfo.driveType,
        nhtsaVehicleInfo.fuelTypePrimary,
      ].filter(Boolean).join(' • ')
    : null;

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to="/vehicles"
        className="inline-flex items-center text-gray-400 hover:text-white transition"
      >
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Vehicles
      </Link>

      {/* A. Vehicle Header */}
      <div className="flex items-center gap-5">
        <VehicleImage vin={vehicle.vin} size="lg" />
        <div>
          <h1 className="text-2xl font-bold text-white">
            {formatVehicle(vehicle.vehicleYear, vehicle.vehicleMake, vehicle.vehicleModel)}
          </h1>
          <p className="text-gray-400 font-mono mt-1">VIN: {formatVin(vehicle.vin)}</p>
          {nhtsaSummary && (
            <p className="text-gray-500 text-sm mt-2">{nhtsaSummary}</p>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Scans"
          value={vehicle.sessionHistory?.length || 0}
          icon={<img src={dashboardIcon} alt="" className="w-6 h-6" />}
        />
        <StatCard
          label="Unique DTCs"
          value={dtcCounts.size}
          icon={<img src={errorIcon} alt="" className="w-6 h-6" />}
        />
        <StatCard
          label="Total DTC Events"
          value={vehicle.dtcHistory?.length || 0}
          icon={<img src={clipboardIcon} alt="" className="w-6 h-6" />}
        />
        <StatCard
          label="Avg DTCs/Scan"
          value={vehicle.sessionHistory && vehicle.sessionHistory.length > 0
            ? Math.round(
                vehicle.sessionHistory.reduce((acc, s) => acc + s.dtcCount, 0) /
                  vehicle.sessionHistory.length
              )
            : 0}
          icon={<img src={chartIcon} alt="" className="w-6 h-6" />}
        />
      </div>

      {/* B. Vehicle Information Card (NHTSA - auto-loaded, expanded by default) */}
      <Card>
        <SectionHeader title="Vehicle Information" />
        {nhtsaLoading ? (
          <p className="text-gray-500 text-sm">Loading vehicle details...</p>
        ) : nhtsaVehicleInfo ? (
          <div className="space-y-6">
            {/* Core Vehicle Info */}
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Vehicle</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <LabelValue label="Year" value={nhtsaVehicleInfo.year || 'N/A'} />
                <LabelValue label="Make" value={nhtsaVehicleInfo.make || 'N/A'} />
                <LabelValue label="Model" value={nhtsaVehicleInfo.model || 'N/A'} />
                <LabelValue label="Trim" value={nhtsaVehicleInfo.trim || 'N/A'} />
                {nhtsaVehicleInfo.series && <LabelValue label="Series" value={nhtsaVehicleInfo.series} />}
                <LabelValue label="Body Type" value={nhtsaVehicleInfo.bodyClass || 'N/A'} />
                <LabelValue label="Doors" value={nhtsaVehicleInfo.doors || 'N/A'} />
                {nhtsaVehicleInfo.vehicleType && <LabelValue label="Vehicle Type" value={nhtsaVehicleInfo.vehicleType} />}
              </div>
            </div>

            {/* Engine & Powertrain */}
            <div className="pt-4 border-t border-white/10">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Engine & Powertrain</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {nhtsaVehicleInfo.engineModel && <LabelValue label="Engine" value={nhtsaVehicleInfo.engineModel} />}
                <LabelValue label="Cylinders" value={nhtsaVehicleInfo.engineCylinders || 'N/A'} />
                <LabelValue label="Displacement" value={nhtsaVehicleInfo.displacementL ? `${nhtsaVehicleInfo.displacementL}L` : 'N/A'} />
                {nhtsaVehicleInfo.engineHp && <LabelValue label="Horsepower" value={`${nhtsaVehicleInfo.engineHp} HP`} />}
                <LabelValue label="Transmission" value={nhtsaVehicleInfo.transmissionStyle || 'N/A'} />
                <LabelValue label="Drivetrain" value={nhtsaVehicleInfo.driveType || 'N/A'} />
                <LabelValue label="Fuel Type" value={nhtsaVehicleInfo.fuelTypePrimary || 'N/A'} />
              </div>
            </div>

            {/* Manufacturer Info */}
            <div className="pt-4 border-t border-white/10">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Manufacturer</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <LabelValue label="Manufacturer" value={nhtsaVehicleInfo.manufacturer || 'N/A'} />
                {nhtsaVehicleInfo.plantCity && <LabelValue label="Plant City" value={nhtsaVehicleInfo.plantCity} />}
                <LabelValue label="Plant Country" value={nhtsaVehicleInfo.plantCountry || 'N/A'} />
              </div>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">Vehicle details unavailable</p>
        )}
      </Card>

      {/* AI Diagnostic Summary + Report Buttons */}
      <AIDiagnosticSummary vin={vin!} />

      {/* C. Vehicle Data Tabs — Tabbed interface for all paid lookups */}
      <Card>
        <VehicleDataTabs vin={vin!} nhtsaVehicleInfo={nhtsaVehicleInfo} />
      </Card>

      {/* D. Scan Sessions */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <SectionHeader title="Scan Sessions" count={allSessions.length} />
          <Link
            to={`/sessions?vehicle=${vin}`}
            className="text-sm text-purple-400 hover:text-purple-300 transition"
          >
            View on Sessions page →
          </Link>
        </div>
        <Table
          columns={sessionColumns}
          data={paginatedSessions}
          keyExtractor={(item) => item.sessionKey}
          onRowClick={(item) => navigate(`/sessions/${item.sessionKey}`)}
          isLoading={!sessionsData}
          emptyMessage="No scan sessions recorded yet"
        />
        {sessionTotalPages > 1 && (
          <Pagination
            currentPage={sessionPage}
            totalPages={sessionTotalPages}
            onPageChange={setSessionPage}
          />
        )}
      </Card>

    </div>
  );
}
